import { useMemo } from 'react'
import { Calendar, ArrowLeftRight, Warehouse, User, Tag } from 'lucide-react'

import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

import {
  DataTableNew,
  generateSkeletonData,
  useSkeletonColumns,
  ListPageHeader,
  PageSizeFilter,
  DateRangeFilter,
  ClearFiltersButton,
  CheckboxFilter,
  CheckboxFilterOption,
} from '@/components/newRedesign/components'
import {
  SearchableFilter,
  SearchableFilterOption,
} from '@/components/newRedesign/components/filters/SearchableFilter'
import { SimpleTablePagination } from '@/components/table/SimpleTablePagination'
import { useMovimientosTableContext } from './TableContext'
import { Movement } from '../interfaces/inventory'
import { useAppSelector } from '@/store/hooks'
import { getUsers } from '@/api/usersAPI'

interface MovimientosDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
}

const operationOptions: CheckboxFilterOption[] = [
  { id: 'all', label: 'Todos los tipos', value: 'all' },
  { id: 'ENTRY', label: 'Entrada', value: 'ENTRY' },
  { id: 'OUT', label: 'Salida', value: 'OUT' },
]

const categoryOptions: CheckboxFilterOption[] = [
  { id: 'all', label: 'Todas las categorías', value: 'all' },
  { id: 'TRASLATE', label: 'Traslado', value: 'TRASLATE' },
  { id: 'SALE', label: 'Venta', value: 'SALE' },
  { id: 'BUY', label: 'Compra', value: 'BUY' },
  { id: 'FAST_ENTRY', label: 'Entrada Rápida', value: 'FAST_ENTRY' },
  { id: 'ADJUST', label: 'Ajuste', value: 'ADJUST' },
  { id: 'DESCOMPOSITION', label: 'Descomposición', value: 'DESCOMPOSITION' },
  { id: 'ELABORATION', label: 'Elaboración', value: 'ELABORATION' },
  { id: 'DISPATCH', label: 'Despacho', value: 'DISPATCH' },
  { id: 'CONTAINER', label: 'Contenedor', value: 'CONTAINER' },
  { id: 'OUT', label: 'Salida', value: 'OUT' },
  { id: 'WASTE', label: 'Desperdicio', value: 'WASTE' },
  { id: 'TRANSFORMATION', label: 'Transformación', value: 'TRANSFORMATION' },
  { id: 'REFUNDED', label: 'Devuelto', value: 'REFUNDED' },
]

export function MovimientosDataTable<TData, TValue>({
  columns,
}: MovimientosDataTableProps<TData, TValue>) {
  const {
    movimientos,
    isLoading,
    isFetching,
    isError,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    refetch,
    filters,
    setFilters,
    clearFilters,
  } = useMovimientosTableContext()

  const allAreas = useAppSelector((state) =>
    state.nomenclator.areas.filter((area) => area.type === 'STOCK')
  )

  const fetchAlmacenes = async (
    search: string
  ): Promise<SearchableFilterOption[]> => {
    const allOption: SearchableFilterOption = {
      id: 'all',
      label: 'Todos',
    }

    const searchTerm = search.trim().toLowerCase()
    const filtered = searchTerm
      ? allAreas.filter((area) => area.name.toLowerCase().includes(searchTerm))
      : allAreas

    const areaOptions = filtered.map((area) => ({
      id: area.id.toString(),
      label: area.name,
    }))

    return [allOption, ...areaOptions]
  }
  const fetchUsers = async (
    search: string
  ): Promise<SearchableFilterOption[]> => {
    try {
      const allOption: SearchableFilterOption = {
        id: 'all',
        label: 'Todos',
      }
      const users = await getUsers({
        page: 1,
        per_page: 20,
        search: search.trim(),
      })

      const userOptions = users
        .filter((user) => user.isActive && user.id && user.displayName)
        .map((user) => ({
          id: user.id.toString(),
          label:
            user.displayName || user.username || user.email || 'Sin nombre',
        }))

      return [allOption, ...userOptions]
    } catch {
      return [
        {
          id: 'all',
          label: 'Todos',
        },
      ]
    }
  }

  const skeletonData = useMemo(() => {
    if (!isLoading && !isFetching) return []
    return generateSkeletonData<Movement>(pageSize)
  }, [isLoading, isFetching, pageSize])

  const data = isLoading || isFetching ? skeletonData : movimientos || []

  const tableColumns = useSkeletonColumns(columns, isLoading || isFetching)

  const table = useReactTable({
    data: data as TData[],
    columns: tableColumns as ColumnDef<TData, TValue>[],
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    state: {
      pagination: {
        pageIndex: currentPage - 1,
        pageSize: pageSize,
      },
    },
  })

  // Handle filter changes
  const handleOperationChange = (values: string[]) => {
    const value = values[0] || 'all'
    setFilters({
      ...filters,
      operationFilter: value,
    })
  }

  const handleAlmacenChange = (
    value: string | number | null | (string | number)[]
  ) => {
    const strValue = value ? value.toString() : 'all'
    setFilters({
      ...filters,
      almacenFilter: strValue,
    })
  }

  const handleUserChange = (
    value: string | number | null | (string | number)[]
  ) => {
    const strValue = value ? value.toString() : 'all'
    setFilters({
      ...filters,
      userFilter: strValue,
    })
  }

  const handleCategoryChange = (values: string[]) => {
    const value = values[0] || 'all'
    setFilters({
      ...filters,
      categoryFilter: value,
    })
  }

  const handleEntryDateFromChange = (date: string) => {
    setFilters((prev) => ({ ...prev, entryDateFrom: date || undefined }))
  }

  const handleEntryDateToChange = (date: string) => {
    setFilters((prev) => ({ ...prev, entryDateTo: date || undefined }))
  }

  const clearAllFilters = () => {
    clearFilters()
  }

  // Check if there are active filters
  const hasActiveFilters =
    !!filters.entryDateFrom ||
    !!filters.entryDateTo ||
    !!(filters.operationFilter && filters.operationFilter !== 'all') ||
    !!(filters.almacenFilter && filters.almacenFilter !== 'all') ||
    !!(filters.userFilter && filters.userFilter !== 'all') ||
    !!(filters.categoryFilter && filters.categoryFilter !== 'all')

  if (isError) {
    return (
      <div className="flex h-32 items-center justify-center text-gray-500">
        Error al cargar los movimientos
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-col">
      {/* Header with Filters - stays on top */}
      <div className="flex-shrink-0 pb-3">
        <ListPageHeader
          secondaryActions={
            <>
              {/* 1. Tipo - Order 1 */}
              <div className="flex-none" style={{ order: 1 }}>
                <CheckboxFilter
                  allOptionId="all"
                  exclusive
                  icon={<ArrowLeftRight className="h-4 w-4" />}
                  onChange={handleOperationChange}
                  options={operationOptions}
                  popoverAlign="start"
                  popoverWidth="180px"
                  title="Tipo"
                  value={[filters.operationFilter || 'all']}
                />
              </div>

              {/* 2. Usuario - Order 2 */}
              <div className="flex-none" style={{ order: 2 }}>
                <SearchableFilter
                  emptyMessage="No se encontraron usuarios"
                  fetcher={fetchUsers}
                  icon={<User className="h-4 w-4" />}
                  name="movimientos-usuario"
                  onChange={handleUserChange}
                  popoverAlign="start"
                  searchPlaceholder="Buscar usuario..."
                  title="Usuario"
                  value={filters.userFilter || 'all'}
                />
              </div>

              {/* 3. Almacén - Order 3 */}
              <div className="flex-none" style={{ order: 3 }}>
                <SearchableFilter
                  emptyMessage="No se encontraron almacenes"
                  fetcher={fetchAlmacenes}
                  icon={<Warehouse className="h-4 w-4" />}
                  name="movimientos-almacen"
                  onChange={handleAlmacenChange}
                  popoverAlign="start"
                  searchPlaceholder="Buscar almacén..."
                  title="Almacén"
                  value={filters.almacenFilter || 'all'}
                />
              </div>

              {/* 4. Categoría - Order 4 */}
              <div className="flex-none" style={{ order: 4 }}>
                <CheckboxFilter
                  allOptionId="all"
                  exclusive
                  icon={<Tag className="h-4 w-4" />}
                  onChange={handleCategoryChange}
                  options={categoryOptions}
                  popoverAlign="start"
                  title="Categoría"
                  value={[filters.categoryFilter || 'all']}
                />
              </div>

              {/* 5. Fecha - Order 5 */}
              <div className="flex-none" style={{ order: 5 }}>
                <DateRangeFilter
                  dateFrom={filters.entryDateFrom || ''}
                  dateTo={filters.entryDateTo || ''}
                  icon={<Calendar className="h-4 w-4" />}
                  onDateFromChange={handleEntryDateFromChange}
                  onDateToChange={handleEntryDateToChange}
                  placeholder="Fecha"
                  popoverAlign="start"
                />
              </div>

              {/* Page Size - Order 6 */}
              <div className="flex-none" style={{ order: 6 }}>
                <PageSizeFilter
                  onChange={setPageSize}
                  options={[10, 20, 30, 40, 50]}
                  pageSize={pageSize}
                  popoverAlign="start"
                />
              </div>

              {/* Clear All Filters Button - Order 7 */}
              <div className="flex-none" style={{ order: 7 }}>
                <ClearFiltersButton
                  onClick={clearAllFilters}
                  visible={hasActiveFilters}
                />
              </div>
            </>
          }
        />
      </div>

      {/* Table container - takes remaining space */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        {/* Loading indicator during search/filtering */}
        {isFetching && !isLoading && (
          <div className="absolute left-0 right-0 top-0 z-20 h-0.5 overflow-hidden bg-gray-100">
            <div className="h-full w-full animate-pulse bg-blue-500" />
          </div>
        )}
        <DataTableNew height="100%" table={table} />
      </div>

      {/* Pagination - fixed height at bottom */}
      <div className="flex-shrink-0 pt-3">
        <SimpleTablePagination
          currentPage={currentPage}
          isLoading={isLoading || isFetching}
          onPageChange={setCurrentPage}
          onRefresh={refetch}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
        />
      </div>
    </div>
  )
}
